import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/use-auth';
import { useCoinBalance } from '@/context/use-coin-balance';

interface FloatingNavProps {
  onOpenTOC: () => void;
  onOpenSettings?: () => void;
  currentChapter?: number;
  bookId?: string;
}

export function FloatingNav({ 
  onOpenTOC, 
  onOpenSettings,
  currentChapter,
  bookId
}: FloatingNavProps) {
  const { user } = useAuth();
  const { balance } = useCoinBalance();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show nav when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div 
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-300 ease-out ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
      }`}
    >
      <div className="flex items-center gap-1 bg-[var(--bg-secondary)]/95 backdrop-blur-xl border border-[var(--border)] rounded-full px-2 py-2 shadow-2xl shadow-black/50">
        {/* TOC Button */}
        <button 
          onClick={onOpenTOC}
          className="p-3 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-all"
          title="Table of Contents"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        </button>

        {/* Settings Button */}
        <button 
          onClick={onOpenSettings}
          className="p-3 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-all"
          title="Reading settings"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </button>

        {/* Chapter indicator (if provided) */}
        {currentChapter && (
          <>
            <div className="w-px h-6 bg-[var(--border)]" />
            <span className="px-3 font-ui text-xs text-[var(--text-tertiary)]">
              Ch. {currentChapter}
            </span>
          </>
        )}

        {/* Token balance (if logged in) - Clickable to go to billing */}
        {user && (
          <>
            <div className="w-px h-6 bg-[var(--border)]" />
            <button
              onClick={() => navigate('/billing', { state: { returnUrl: bookId ? `/book/${bookId}/${currentChapter || 1}` : '/' } })}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full hover:bg-[var(--bg-tertiary)] transition-all"
              title="Get more coins"
            >
              <svg className="w-3.5 h-3.5 text-[var(--accent)]" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
                <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="bold">L</text>
              </svg>
              <span className="font-ui text-xs font-medium text-[var(--text-secondary)]">
                {balance ?? user.tokenBalance}
              </span>
              <svg className="w-3 h-3 text-[var(--text-tertiary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
